import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

type UseHideOnScrollOptions = {
  /** Minimum delta (px) before treating movement as a direction change. */
  threshold?: number;
  /** Always show the chrome when the scrolled target is within this many px of its top. */
  topOffset?: number;
};

function resolveScrollTarget(target: EventTarget | null): Element | Window {
  if (target instanceof Element) {
    if (
      target === document.documentElement ||
      target === document.body ||
      // Some browsers report the Document itself as the scroll target.
      (target as unknown) === document
    ) {
      return window;
    }
    return target;
  }
  return window;
}

function readScrollY(target: Element | Window): number {
  return target === window ? window.scrollY || 0 : (target as Element).scrollTop;
}

/**
 * Returns true when the user is scrolling down (chrome should hide) and false
 * when scrolling up or near the top of the page (chrome should show).
 *
 * Listens with capture so nested overflow containers (common in page layouts)
 * still drive the behavior, not only window scroll.
 */
export function useHideOnScroll({
  threshold = 8,
  topOffset = 24,
}: UseHideOnScrollOptions = {}): boolean {
  const [hidden, setHidden] = useState(false);
  // Per-target last positions so a nested scroller and the window don't
  // contaminate each other's deltas.
  const lastYByTargetRef = useRef(new WeakMap<Element | Window, number>());
  const location = useLocation();

  // Re-show on navigation so a newly opened page never starts with chrome hidden.
  useEffect(() => {
    setHidden(false);
    lastYByTargetRef.current = new WeakMap();
  }, [location.pathname]);

  useEffect(() => {
    const onScroll = (event: Event) => {
      // The workbench event list scrolls inside a fixed shell. Folding the
      // phone header and bottom nav in response resizes the viewport on
      // Android and the two bars shake for the rest of the gesture.
      if (event.target instanceof Element && event.target.closest("[data-keep-chrome]")) return;
      const target = resolveScrollTarget(event.target);
      const y = readScrollY(target);
      const lastY = lastYByTargetRef.current.get(target);

      // First observation for this target: seed baseline only. A previous bug
      // used `?? y` here, which made delta always 0 and permanently skipped
      // the hide/show update.
      if (lastY === undefined) {
        lastYByTargetRef.current.set(target, y);
        if (y <= topOffset) {
          setHidden(false);
        }
        return;
      }

      const delta = y - lastY;
      if (Math.abs(delta) < threshold) {
        return;
      }

      lastYByTargetRef.current.set(target, y);

      if (y <= topOffset) {
        setHidden(false);
        return;
      }

      setHidden(delta > 0);
    };

    window.addEventListener("scroll", onScroll, { capture: true, passive: true });
    return () => window.removeEventListener("scroll", onScroll, { capture: true });
  }, [threshold, topOffset]);

  return hidden;
}
